/** 상품유형 구분 */
export const ProductClass = {
    /** 단일상품 */
    SINGLE: 'SINGLE',
    /** 추가구성상품 */
    SUPPLEMENTED: 'SUPPLEMENTED',
    /** 옵션상품 */
    OPTIONAL: 'OPTIONAL',
    /** 조합형옵션상품 */
    PRICING_OPTIONAL: 'PRICING_OPTIONAL',
} as const

export type ProductClassType = Constant<typeof ProductClass>

/** 주문제작 구분 */
export const Custom = {
    /** 맞춤제작(구매자동의필요) */
    CUSTOM_ORDER: 'CUSTOM_ORDER',
    /** 주문확인 후 제작 */
    AFTER_CONFIRM: 'AFTER_CONFIRM',
    /** 주문확인 후 제작 + 맞춤제작(구매자동의필요) */
    CUSTOM_ORDER_AFTER_CONFIRM: 'CUSTOM_ORDER_AFTER_CONFIRM',
} as const

export type CustomType = Constant<typeof Custom>

export const CustomOrderDetailMap: Record<CustomType, string> = {
    [Custom.AFTER_CONFIRM]: '주문확인 후 제작',
    [Custom.CUSTOM_ORDER]: '맞춤제작',
    [Custom.CUSTOM_ORDER_AFTER_CONFIRM]: '맞춤제작',
}

export const CustomOrders: CustomType[] = [Custom.CUSTOM_ORDER, Custom.CUSTOM_ORDER_AFTER_CONFIRM]

/** 주문서 상품 아이템 옵션 정보 */
export const ProductElement = {
    // 가격이 있는 옵션 상품 V2
    COMBINATION: 'COMBINATION',
    // 직접 입력형 옵션 V2
    CUSTOM: 'CUSTOM',
    // 가격이 없는 단순 옵션
    OPTION: 'OPTION',
    // 추가 상품 V2
    SUPPLEMENT: 'SUPPLEMENT',
    // 표준형 옵션 (COMBINATION 과 동일)
    STANDARD: 'STANDARD',
    // 지점옵션 (장보기)
    BRANCH: 'BRANCH',
} as const

export type ProductElementType = Constant<typeof ProductElement>

export const DesiredDeliveryCase = {
    SHEET: 'SHEET',
    RESULT: 'RESULT',
    STATUS: 'STATUS',
    CART: 'CART',
} as const

export type DesiredDeliveryCaseType = Constant<typeof DesiredDeliveryCase>

/** 장보기 혜택 유형 */
export const BranchBenefit = {
    /** N+1 */
    PLUS_1: 'PLUS_1',
    /** 수량무시할인 */
    QUANTITY_IGNORE: 'QUANTITY_IGNORE',
} as const

export type BranchBenefitType = Constant<typeof BranchBenefit>

/** 선물하기 수신자 */
export const GiftingReceiver = {
    /** 네이버ID */
    RCVTP_001: 'RCVTP_001',
    /** 휴대폰 번호 */
    RCVTP_002: 'RCVTP_002',
    /** 카카오톡 */
    RCVTP_003: 'RCVTP_003',
} as const

export type GiftingReceiverType = Constant<typeof GiftingReceiver>

/** 선물 수락 상태 */
export const GiftReceivingStatus = {
    /** 수락 대기 중 */
    WAIT_FOR_RECEIVING: 'WAIT_FOR_RECEIVING',
    /** 수락완료 */
    RECEIVED: 'RECEIVED',
} as const

export type GiftReceivingStatusType = Constant<typeof GiftReceivingStatus>

/** 장보기 보증금 */
export const BranchDeposit = {
    /** 알비백 */
    COOL_BAG: 'COOL_BAG',
    /** 리유저블 백 */
    REUSABLE_BAG: 'REUSABLE_BAG',
    /** 일회용 컵 */
    SINGLE_USE_CUP: 'SINGLE_USE_CUP',
    /** 리유저블 컵 */
    REUSABLE_CUP: 'REUSABLE_CUP',
    /** 봉투 */
    PLASTIC_BAG: 'PLASTIC_BAG',
    /** 없음 */
    NONE: 'NONE',
} as const

export type BranchDepositType = Constant<typeof BranchDeposit>

/** 오늘출발 불가사유 */
export const TodayDispatchNotAppliedReason = {
    /** 오늘출발설정없음(일반상품) */
    NO_THIS_DAY_DISPATCH_CONFIG: 'NO_THIS_DAY_DISPATCH_CONFIG',
    /** 판매자휴무요일 */
    HOLIDAY_OF_THE_WEEK: 'HOLIDAY_OF_THE_WEEK',
    /** 영업일이아님 */
    NO_SALES_DAY: 'NO_SALES_DAY',
    /** 판매자휴무일 */
    SELLER_HOLIDAY: 'SELLER_HOLIDAY',
    /** 기준시간지남 */
    OVERDUE_TIME: 'OVERDUE_TIME',
    /** 재고사유(부족, 품절)는 일반상품처리 */
    NOT_ENOUGH_STOCK: 'NOT_ENOUGH_STOCK',
    /** 재고사유(부족, 품절)는 일반상품처리 */
    OUT_OF_STOCK: 'OUT_OF_STOCK',
} as const

export type TodayDispatchNotAppliedReasonType = Constant<typeof TodayDispatchNotAppliedReason>

export const ProductModalCase = {
    /** 쿠폰 선택 모달 */
    COUPON_SELECT: 'COUPON_SELECT',
    /** 주문제작 툴팁 */
    CUSTOM_ORDER_TOOLTIP: 'CUSTOM_ORDER_TOOLTIP',
    /** 오늘출발 툴팁 */
    TODAY_DISPATCH_TOOLTIP: 'TODAY_DISPATCH_TOOLTIP',
    /** 알비백 툴팁 */
    RB100_TOOLTIP: 'RB100_TOOLTIP',
    /** 안전결제 툴팁 */
    SAFTEY_PURCHASE_TOOLTIP: 'SAFTEY_PURCHASE_TOOLTIP',
    /** 리유저블컵 툴팁 */
    REUSEABLE_CUP_TOOLTIP: 'REUSEABLE_CUP_TOOLTIP',
    /** 일회용컵 툴팁 */
    DISPOSABLE_CUP_TOOLTIP: 'DISPOSABLE_CUP_TOOLTIP',
    /** 희망일배송 지역별 배송비 안내 툴팁 */
    HOPE_DELIVERY_TOOLTIP: 'HOPE_DELIVERY_TOOLTIP',
    /** 추가 배송비 안내 툴팁 (C1 1.0) */
    ADDITIONAL_DELIVERY_FEE_TOOLTIP: 'ADDITIONAL_DELIVERY_FEE_TOOLTIP',
    /**
     * 상세 영역에서 사용되는 모달 Case
     */
    /** 가맹점 문의하기 모달 */
    MERCHANT_INQUIRY_MODAL: 'MERCHANT_INQUIRY_MODAL',
    /** 과업버튼 내 재구매 모달 */
    REPURCHASE_MODAL: 'REPURCHASE_MODAL',
    /** 배송희망일 변경 사유 모달 */
    DELIVERY_CHANGE_REASON: 'DELIVERY_CHANGE_REASON',
    /** 배송 시뮬레이터 예측 모달 */
    DELIVERY_PREDICTION: 'DELIVERY_PREDICTION',
    /** 도착보장 > 배송지연 툴팁 모달 */
    DELIVERY_GUARANTEE_DELAYED: 'DELIVERY_GUARANTEE_DELAYED',
    /** 도착보장 배송 안내 모달 */
    DELIVERY_GUARANTEE_NOTIFICATION: 'DELIVERY_GUARANTEE_NOTIFICATION',
    REPURCHASE_AMOUNT_OVER_MODAL: 'REPURCHASE_AMOUNT_OVER_MODAL',
    REPURCHASE_CART_ERROR: 'REPURCHASE_CART_ERROR',
    REPURCHASE_ERROR: 'REPURCHASE_ERROR',
    REPURCHASE_INFO_CHANGE: 'REPURCHASE_INFO_CHANGE',
    REPURCHASE_UNABLE: 'REPURCHASE_UNABLE',
    RETURN_REQUEST: 'RETURN_REQUEST',
} as const

export type ProductModalCaseValue = Constant<typeof ProductModalCase>

/** 상품주문상태 */
export const ProductOrderStatus = {
    /** 결제 대기 */
    PAYMENT_WAITING: 'PAYMENT_WAITING',
    /** 미결제로 인한 취소 */
    CANCELED_BY_NOPAYMENT: 'CANCELED_BY_NOPAYMENT',
    /** 이미 결제 */
    PAYED: 'PAYED',
    /** 배송중 */
    DELIVERING: 'DELIVERING',
    /** 배송완료 */
    DELIVERED: 'DELIVERED',
    /** 구매 결정 */
    PURCHASE_DECIDED: 'PURCHASE_DECIDED',
    /** 취소완료 */
    CANCELED: 'CANCELED',
    /** 반송완료 */
    RETURNED: 'RETURNED',
    /** 교환완료 */
    EXCHANGED: 'EXCHANGED',
    /** 결제 오류 */
    PAYMENT_ERROR: 'PAYMENT_ERROR',
    /** 해당 없음 */
    NONE: 'NONE',
} as const

export type ProductOrderStatusType = Constant<typeof ProductOrderStatus>

/** 희망배송일 변경처리자 */
export const HopeDeliveryPerson = {
    PURCHASER: 'PURCHASER',
    SELLER: 'SELLER',
    ADM: 'ADM',
    SYSTEM: 'SYSTEM',
    API: 'API',
}

export type HopeDeliveryPersonType = Constant<typeof HopeDeliveryPerson>

/** 장보기 마켓 타입 */
export const BranchMarket = {
    HOMEPLUS: 'HOMEPLUS',
    GS_FRESH: 'GS_FRESH',
    NH_HANARO: 'NH_HANARO',
    CHOROC: 'CHOROC',
    ORGA: 'ORGA',
    ABOUTPET: 'ABOUTPET',
    EMART: 'EMART',
    EMART_TRADERS: 'EMART_TRADERS',
    SSG: 'SSG',
    FREDIT: 'FREDIT',
    HELLO_NATURE: 'HELLO_NATURE',
    ABOUT_PET: 'ABOUT_PET',
    PET_FRIENDS: 'PET_FRIENDS',
    HOMEPLUS_EXPRESS: 'HOMEPLUS_EXPRESS',
    GS_THEFRESH: 'GS_THEFRESH',
    EMART_EVERYDAY: 'EMART_EVERYDAY',
    TOMATO: 'TOMATO',
    GS25: 'GS25',
    CU: 'CU',
    EMART_EVERYDAY_QUICK: 'EMART_EVERYDAY_QUICK',
    SUPER_KITCHEN: 'SUPER_KITCHEN',
    KURLYNMART: 'KURLYNMART',
    POLYPARK: 'POLYPARK',
    SNOWFOXFLOWER: 'SNOWFOXFLOWER',
    JANGBOGOMART: 'JANGBOGOMART',
    WINEPICKS: 'WINEPICKS',
    WINEANDMORE: 'WINEANDMORE',
    ALPHA: 'ALPHA',
} as const

export type BranchMarketType = Constant<typeof BranchMarket>

export const BranchDelivery = {
    DELIVERY_SLOT: 'DELIVERY_SLOT',
    EXPRESS_DELIVERY: 'EXPRESS_DELIVERY',
    QUICK_COMMERCE: 'QUICK_COMMERCE',
    KURLY: 'KURLY',
} as const

export type BranchDeliveryType = Constant<typeof BranchDelivery>

/**
 * 장보기 배송 스케줄 타입
 * - ⚠️ 현재 `컬리` 에서만 사용중
 */
export const BranchDeliverySchedule = {
    /** 정상배송 */
    NORMAL: 'NORMAL',
    /** 예정된 배송일보다 하루 더 늦게 배송 */
    CONTINUITY: 'CONTINUITY',
    /** 주문마감시간이 지났으나 주문마감시간 전에 주문한 것 처럼 배송가능 */
    EXTENSION: 'EXTENSION',
    /** 휴무일로 인해 배송예정일이 미래의 특정 날짜로 지정 */
    HOLIDAY: 'HOLIDAY',
    /** 다음날 새벽보다 일찍 배송 */
    EARLYBIRD: 'EARLYBIRD',
} as const

export type BranchDeliveryScheduleType = Constant<typeof BranchDeliverySchedule>

/**
 * 장보기 배송 태그 타입
 * - ⚠️ 현재 `컬리` 에서만 사용중
 */
export const OrderDeliveryTag = {
    TODAY: 'TODAY',
    TOMORROW: 'TOMORROW',
    DAWN: 'DAWN',
} as const

export type OrderDeliveryTagType = Constant<typeof OrderDeliveryTag>

/** 클레임 구분 */
export const Claim = {
    CANCEL: 'CANCEL',
    RETURN: 'RETURN',
    EXCHANGE: 'EXCHANGE',
    PURCHASE_DECISION_HOLDBACK: 'PURCHASE_DECISION_HOLDBACK',
    ADMIN_CANCEL: 'ADMIN_CANCEL',
} as const

export type ClaimType = Constant<typeof Claim>

/** 클레임 보류유형 */
export const HoldClass = {
    RETURN_DELIVERYFEE: 'RETURN_DELIVERYFEE',
    EXTRAFEEE: 'EXTRAFEEE',
    RETURN_DELIVERYFEE_AND_EXTRAFEEE: 'RETURN_DELIVERYFEE_AND_EXTRAFEEE',
    RETURN_PRODUCT_NOT_DELIVERED: 'RETURN_PRODUCT_NOT_DELIVERED',
    ETC: 'ETC',
    EXCHANGE_DELIVERYFEE: 'EXCHANGE_DELIVERYFEE',
    EXCHANGE_EXTRAFEE: 'EXCHANGE_EXTRAFEE',
    EXCHANGE_PRODUCT_READY: 'EXCHANGE_PRODUCT_READY',
    EXCHANGE_PRODUCT_NOT_DELIVERED: 'EXCHANGE_PRODUCT_NOT_DELIVERED',
    SELLER_CONFIRM_NEED: 'SELLER_CONFIRM_NEED',
    PURCHASER_CONFIRM_NEED: 'PURCHASER_CONFIRM_NEED',
    SELLER_REMIT: 'SELLER_REMIT',
    ETC2: 'ETC2',
    EXCHANGE_HOLDBACK: 'EXCHANGE_HOLDBACK',
} as const

export type HoldClassType = Constant<typeof HoldClass>

/**
 * @TODO 톡톡하기 말풍선 push에 필요한 데이터
 * 추후 반영 필요
 **/
export const ClaimStatus = {
    CANCEL_REQUEST: 'CANCEL_REQUEST',
    CANCELING: 'CANCELING',
    CANCEL_DONE: 'CANCEL_DONE',
    CANCEL_REJECT: 'CANCEL_REJECT',
    RETURN_REQUEST: 'RETURN_REQUEST',
    EXCHANGE_REQUEST: 'EXCHANGE_REQUEST',
    COLLECTING: 'COLLECTING',
    COLLECT_DONE: 'COLLECT_DONE',
    EXCHANGE_REDELIVERING: 'EXCHANGE_REDELIVERING',
    RETURN_DONE: 'RETURN_DONE',
    EXCHANGE_DONE: 'EXCHANGE_DONE',
    RETURN_REJECT: 'RETURN_REJECT',
    EXCHANGE_REJECT: 'EXCHANGE_REJECT',
    PURCHASE_DECISION_HOLDBACK: 'PURCHASE_DECISION_HOLDBACK',
    PURCHASE_DECISION_REQUEST: 'PURCHASE_DECISION_REQUEST',
    PURCHASE_DECISION_HOLDBACK_RELEASE: 'PURCHASE_DECISION_HOLDBACK_RELEASE',
    ADMIN_CANCELING: 'ADMIN_CANCELING',
    ADMIN_CANCEL_DONE: 'ADMIN_CANCEL_DONE',
    ADMIN_CANCEL_REJECT: 'ADMIN_CANCEL_REJECT',
} as const

export type ClaimStatusType = Constant<typeof ClaimStatus>

export const PRODUCTS_ECOUPON_LINKED_STATE = {
    ALL: 'ALL',
    MIXED: 'MIXED',
    NONE: 'NONE',
} as const

/**
 * 배송 태그
 * N배송 대상으로만 내려오는 값이 아님
 */
export const DeliveryTagType = {
    /** 새벽배송 */
    DAWN: 'DAWN',
    /** 희망일배송 (N희망일배송) */
    HOPE: 'HOPE',
    /** 일반배송(D+2 이상) */
    STANDARD: 'STANDARD',
    /** 일요배송 */
    SUNDAY: 'SUNDAY',
    /** 오늘배송 */
    TODAY: 'TODAY',
    /** 내일배송 */
    TOMORROW: 'TOMORROW',
} as const

export type DeliveryTagTypeValues = Constant<typeof DeliveryTagType>

export const SellerExceptionalCategory = {
    /** 일반 */
    NONE: 'NONE',
    /** 도서 */
    BOOK: 'BOOK',
    /** 주류 (퀵커머스에만 존재하는 카테고리, C2 only) */
    ALCOHOL: 'ALCOHOL',
    /** 전통주 */
    TRADITIONAL_ALCOHOL: 'TRADITIONAL_ALCOHOL',
} as const

export type SellerExceptionalCategoryType = Constant<typeof SellerExceptionalCategory>
